// src/components/MigrationTool.tsx
import React, { useState, useEffect } from 'react';
import { MigrationService } from '../utils/migration.service';

const MigrationTool: React.FC = () => {
  const [migrationStatus, setMigrationStatus] = useState<any>(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [password, setPassword] = useState('');
  const [showMigration, setShowMigration] = useState(false);

  useEffect(() => {
    checkMigration();
  }, []);

  const checkMigration = async () => {
    const status = await MigrationService.checkMigrationStatus();
    setMigrationStatus(status);
    setShowMigration(status.needsMigration);
  };

  const handleMigration = async () => {
    if (!password || password.length < 12) {
      alert('Please enter a secure password (min 12 characters)');
      return;
    }

    setIsMigrating(true);
    
    const result = await MigrationService.performFullMigration(
  password,
  (step: string, prog: number) => {  // Add explicit types
    setProgressMessage(step);
    setProgress(prog);
  }
);

    if (result.success) {
      alert('Migration completed successfully!');
      setShowMigration(false);
      window.location.reload();
    } else {
      alert(`Migration failed: ${result.message}`);
    }
    
    setIsMigrating(false);
  };

  if (!showMigration) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-gray-900 p-8 rounded-xl max-w-lg w-full">
        <h2 className="text-2xl font-bold mb-4">Data Migration Required</h2>
        
        <div className="mb-6 p-4 bg-yellow-900/30 border border-yellow-600 rounded">
          <p className="text-yellow-400 mb-2">
            ⚠️ We've upgraded to a more secure architecture!
          </p>
          <p className="text-sm text-gray-300">
            Your wallet data needs to be migrated to the new encrypted storage system. 
            This is a one-time process.
          </p>
        </div>

        {migrationStatus && (
          <div className="mb-4 text-sm">
            <p>✓ Legacy data found: {migrationStatus.hasLegacyData ? 'Yes' : 'No'}</p>
            <p>✓ Backend available: {migrationStatus.hasBackendConnection ? 'Yes' : 'No'}</p>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">
            Create a strong password for your bundles:
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-gray-800 rounded px-4 py-2"
            placeholder="Min 12 characters, mixed case + numbers"
            disabled={isMigrating}
          />
        </div>

        {isMigrating && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{progressMessage}</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleMigration}
            disabled={isMigrating || !password}
            className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 rounded disabled:opacity-50"
          >
            {isMigrating ? 'Migrating...' : 'Start Migration'}
          </button>
          <button
            onClick={() => setShowMigration(false)}
            disabled={isMigrating}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
          >
            Skip
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          Note: Skipping migration may result in loss of existing wallet data.
        </p>
      </div>
    </div>
  );
};

export default MigrationTool;